import { useConfig } from '@revenuewell/logic-config';
import { useOidcWithAdmin } from '@revenuewell/logic-oidc';
import AppointmentService from 'apps/wrapper/src/services/appointment/appointment-service';
import { AppointmentRequestDetails } from 'apps/wrapper/src/services/appointment/types/appointment-request-details';
import { EventApptRequestIvStatusUpdated } from 'apps/wrapper/src/types/event-bus';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useEventBus } from '../../../../../hooks/use-event-bus';
import { useInsuranceApi } from '../../../../../hooks/use-insurance-api';
import CarrierSummary from '../../../../../services/insurance/types/carrier-name';
import { IvSummaryStatus } from '../../../../../services/insurance/types/iv-summary-status';
import { Provider } from '../../../../../services/insurance/types/provider';
import { VerificationSummary } from '../../../../../services/insurance/types/verification-summary';
import { ApptRequestIvStatusDialogContext, ApptRequestIvStatusDialogContextProps } from './context';

export interface ApptRequestIvStatusDialogProviderProps {
  opened: boolean;
  onClose: () => void;
  locationId: number;
  appointmentRequestId: number;
  children?: React.ReactNode;
}

export const ApptRequestIvStatusDialogProvider: FC<ApptRequestIvStatusDialogProviderProps> = (props) => {
  const { opened, onClose, appointmentRequestId, locationId } = props;
  const { config } = useConfig();
  const { oidcService } = useOidcWithAdmin();
  const { publish, publishToIframes } = useEventBus();
  const { getProviders, getCarrierSummaries, getAppointmentRequestVerificationSummaries } = useInsuranceApi();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isReady, setIsReady] = useState<boolean>(false);
  const [ivSummary, setIvSummary] = useState<VerificationSummary>();
  const [appointmentRequest, setAppointmentRequest] = useState<AppointmentRequestDetails>();
  const [providers, setProviders] = useState<Provider[]>([]);
  const [carrierSummaries, setCarrierSummaries] = useState<CarrierSummary[]>([]);
  const appointmentApi = useMemo(() => new AppointmentService(config, oidcService), [config, oidcService]);

  useEffect(() => {
    const initApptRequestFlow = async () => {
      setIsLoading(true);

      const [ivSummaries, apptRequest, prov, carriers] = await Promise.all([
        getAppointmentRequestVerificationSummaries([appointmentRequestId], locationId),
        appointmentApi.getAppointmentRequestDetails(appointmentRequestId, locationId),
        getProviders(locationId),
        getCarrierSummaries(locationId)
      ]);

      setIvSummary(ivSummaries[0]?.verificationSummary);
      setAppointmentRequest(apptRequest);
      setProviders(prov);
      setCarrierSummaries(carriers);
      setIsLoading(false);
      setIsReady(true);
    };

    initApptRequestFlow();
  }, [appointmentRequestId, locationId]);

  const getProvider = useCallback(
    (id?: number) => {
      if (!id) return null;
      return providers.find((p) => p.id === id);
    },
    [providers]
  );

  const getCarrierSummary = useCallback(
    (carrierNameId?: number) => {
      if (!carrierNameId) return null;
      return carrierSummaries.find((c) => c.idCarrierName === carrierNameId);
    },
    [carrierSummaries]
  );

  const sendIvStatusUpdatedEvent = useCallback(
    (status: IvSummaryStatus) => {
      const event = {
        messageType: 'apptRequestIvStatusUpdated',
        data: {
          apptRequestId: appointmentRequestId,
          status
        }
      } as EventApptRequestIvStatusUpdated;
      publish(event);
      publishToIframes(event);
    },
    [appointmentRequestId, locationId, publish, publishToIframes]
  );

  const contextValue = {
    opened,
    onClose,
    isReady,
    isLoading,
    setIsLoading,
    locationId,
    appointmentRequest,
    ivSummary,
    providers,
    getProvider,
    getCarrierSummary,
    sendIvStatusUpdatedEvent
  } as ApptRequestIvStatusDialogContextProps;

  return (
    <ApptRequestIvStatusDialogContext.Provider value={contextValue}>
      {props.children}
    </ApptRequestIvStatusDialogContext.Provider>
  );
};
